import { schemaAtPointer, parsePointer, buildPointer } from '@bliksys/schema-engine';

import { useStore, useAppState } from '../hooks/useStore.js';
import { APPLY_BLOCKED_TOAST } from '../messages.js';

import { confirmThreeWay } from './ConfirmDialog.js';
import { toast } from './Toast.js';
import './Breadcrumb.css';

/**
 * Breadcrumb — shows the path from root to the current pointer.
 *
 * Each crumb is a button that navigates to that ancestor. We compute each
 * ancestor's label by walking the schema at that depth so list items
 * display their summary ("About us") rather than the raw pointer segment.
 */
export function Breadcrumb(): JSX.Element {
  const store = useStore();
  const { schema, data, pointer, draftPointer, draftKind } = useAppState();
  const segments = parsePointer(pointer);

  /**
   * Navigate to a breadcrumb's pointer. When inside a draft sub-tree
   * and the target is OUTSIDE that sub-tree, prompt the user with a
   * three-way choice (Apply / Discard / Cancel). Inside-draft navigation
   * — descending further — needs no prompt.
   *
   * If Apply is chosen but the draft has validation issues, the store
   * refuses; we surface a toast and stay put. The user must fix the
   * issue or Discard before they can leave.
   */
  const handleNavigate = (target: string): void => {
    if (draftPointer === null || target === draftPointer || target.startsWith(`${draftPointer}/`)) {
      // No draft, OR target is inside the draft sub-tree → free navigation.
      store.navigate(target);
      return;
    }
    // Target leaves the draft. If the user hasn't actually edited
    // anything in the draft (just zoomed in to look), silently clear
    // the draft and proceed — there's nothing to apply or discard, and
    // a prompt would just be noise. This is the fix for "says it has
    // unapplied changes when it doesn't."
    if (!store.draftHasChanges()) {
      store.clearDraft();
      store.navigate(target);
      return;
    }
    const isCreate = draftKind === 'create';
    void confirmThreeWay({
      title: isCreate ? 'New item not yet applied' : 'Unapplied changes',
      message: isCreate
        ? 'Apply this new item to keep it, or discard it before navigating away.'
        : 'Apply your edits to keep them, or discard them before navigating away.',
      primaryLabel: 'Apply',
      secondaryLabel: 'Discard',
      secondaryDanger: true,
    }).then((choice) => {
      if (choice === 'cancel') return;
      if (choice === 'primary') {
        const ok = store.applyDraft();
        if (!ok) {
          // Validation issues blocked the apply. Stay put with feedback.
          toast(APPLY_BLOCKED_TOAST, 'error');
          return;
        }
        // applyDraft already navigated us to the draft's parent. If the
        // breadcrumb target is somewhere else, fire navigate now.
        if (target !== getParent(draftPointer)) store.navigate(target);
      } else {
        // 'secondary' = Discard
        store.discardDraft();
        if (target !== getParent(draftPointer)) store.navigate(target);
      }
    });
  };

  const crumbs: { pointer: string; label: string }[] = [];

  // Root crumb
  crumbs.push({
    pointer: '',
    label: schema.title ?? 'Root',
  });

  for (let i = 0; i < segments.length; i++) {
    const ancestorPointer = buildPointer(segments.slice(0, i + 1));
    let label: string;
    let skipCrumb = false;
    try {
      const { schema: sub, value } = schemaAtPointer(ancestorPointer, schema, data);
      // If this ancestor is a field whose parent shows it inline-expanded
      // (e.g. `properties` and `items` in the schema editor), and there's
      // a deeper segment past it, skip the crumb. The segment is a path
      // technicality — the user authored the children directly inside the
      // parent's view, so a separate "Properties" crumb adds no signal.
      const isInlineExpandedField =
        typeof sub === 'object' && sub !== null && sub['x-bliksys']?.layout === 'inline';
      const isIntermediate = i < segments.length - 1;
      if (isInlineExpandedField && isIntermediate) {
        skipCrumb = true;
      }
      label = labelFor(segments[i]!, sub, value, schema);
    } catch {
      label = segments[i]!;
    }
    if (!skipCrumb) crumbs.push({ pointer: ancestorPointer, label });
  }

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={crumb.pointer} className="breadcrumb-item">
              {isLast ? (
                <span className="breadcrumb-current" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="breadcrumb-link"
                    onClick={() => handleNavigate(crumb.pointer)}
                    title={i === crumbs.length - 2 ? 'Press Esc to zoom out' : undefined}
                  >
                    {crumb.label}
                  </button>
                  <span className="breadcrumb-separator" aria-hidden="true">
                    /
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function labelFor(
  segment: string,
  schema: unknown,
  value: unknown,
  rootSchema?: unknown,
): string {
  if (typeof schema !== 'object' || schema === null) return segment;
  const s = schema as {
    title?: string;
    'x-bliksys'?: { label?: string; summaryField?: string };
    oneOf?: { $ref?: string; title?: string; properties?: Record<string, { const?: unknown }> }[];
    discriminator?: { propertyName?: string };
  };

  // For list items the segment is numeric — prefer the item's summary.
  // Two distinct things go into a "good" label here:
  //  1. A meaningful summary derived from the item's data (the page's
  //     title, the heading's text, etc.). The schema author can declare
  //     this via x-bliksys.summaryField; otherwise we try a few common
  //     naming keys as a fallback.
  //  2. When (1) yields nothing AND the item is a tagged-union, fall
  //     back to the active variant's TYPE — "Heading" / "Image" /
  //     "Columns" reads better in breadcrumbs than "#1, #1, #1, #1".
  //  3. Last resort, "#N" where N is 1-indexed (still better than the
  //     raw numeric segment).
  if (/^\d+$/.test(segment)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      const hint = s['x-bliksys']?.summaryField;
      if (hint && typeof obj[hint] === 'string') return obj[hint];
      for (const key of ['title', 'name', 'label', 'heading', 'content']) {
        const v = obj[key];
        if (typeof v === 'string' && v.length > 0) {
          // Trim long strings — the "content" field on a paragraph block
          // can be a sentence; we want a short crumb, not a full line.
          return v.length > 40 ? `${v.slice(0, 40)}…` : v;
        }
      }
      // Tagged-union fallback: when the parent's items schema is a
      // oneOf with a discriminator, find the variant whose discriminator
      // value matches this item's, and use its title. Without this the
      // breadcrumb shows "#1" for blocks; with it, the user sees
      // "Heading" / "Image" / "Columns" — the actual content shape.
      const variantTitle = inferVariantTitle(s, obj, rootSchema);
      if (variantTitle) return variantTitle;
    }
    return `#${Number(segment) + 1}`;
  }

  // x-bliksys.label always wins.
  if (s['x-bliksys']?.label !== undefined) return s['x-bliksys'].label;

  // Prefer the schema's authored title. The walker preserves wrapper-side
  // annotations when stepping through `$ref`, so even fields authored as
  // `{ title: 'If', $ref: '#/$defs/Schema' }` surface "If" here (not the
  // referenced definition's title). It also overlays a defaultTitle from
  // the segment name for additionalProperties entries, so $defs entries
  // surface as their authored key (e.g. "Block") instead of the
  // meta-schema's recursive "Schema".
  //
  // Falls through to humanizing the segment only when the schema has no
  // title at all — typically for slots that genuinely have no authored
  // identity (untyped sub-schemas, raw additionalProperties without a
  // useful default).
  if (s.title) return s.title;

  return humanize(segment);
}

/**
 * For a tagged-union (oneOf with a discriminator) item, find the
 * variant whose discriminator value matches the item's value and return
 * its title. Returns undefined when no variant matches or the schema
 * isn't a tagged union — caller falls back to "#N".
 *
 * Variants are typically authored as `{ $ref: '#/$defs/HeadingBlock' }` —
 * we need to resolve those refs before we can read each variant's
 * discriminator-pinning const and title. The optional `rootSchema`
 * supports that resolution; without it we can only handle inline
 * variants.
 */
function inferVariantTitle(
  s: {
    oneOf?: { $ref?: string; title?: string; properties?: Record<string, { const?: unknown }> }[];
    discriminator?: { propertyName?: string };
  },
  obj: Record<string, unknown>,
  rootSchema: unknown,
): string | undefined {
  if (!s.oneOf || s.oneOf.length === 0) return undefined;
  // Discriminator key: the schema-declared one wins; otherwise try
  // 'type' as the convention, since it's what most tagged unions in
  // the wild use (and what Bliksys's demo uses).
  const propName = s.discriminator?.propertyName ?? 'type';
  const tagValue = obj[propName];
  if (typeof tagValue !== 'string') return undefined;
  for (const wrapper of s.oneOf) {
    const variant = resolveLocalRef(wrapper, rootSchema) ?? wrapper;
    const constValue = variant.properties?.[propName]?.const;
    if (constValue === tagValue && variant.title) return variant.title;
  }
  return undefined;
}

/**
 * Tiny local-ref resolver, scoped to what the breadcrumb needs. The
 * engine has a fully-featured `resolveSchema`, but the breadcrumb path
 * runs on every render and we want the cost low — a JSON-pointer walk
 * against the rootSchema is enough for the common case (`#/$defs/X`).
 * Returns undefined when the ref can't be followed; caller falls back
 * to the wrapper.
 */
function resolveLocalRef(
  wrapper: { $ref?: string; title?: string; properties?: Record<string, { const?: unknown }> },
  rootSchema: unknown,
): { title?: string; properties?: Record<string, { const?: unknown }> } | undefined {
  if (!wrapper.$ref?.startsWith('#/')) return undefined;
  if (typeof rootSchema !== 'object' || rootSchema === null) return undefined;
  const segments = wrapper.$ref
    .slice(2)
    .split('/')
    .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));
  let cursor: unknown = rootSchema;
  for (const seg of segments) {
    if (typeof cursor !== 'object' || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[seg];
  }
  return typeof cursor === 'object' && cursor !== null ? cursor : undefined;
}

function humanize(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getParent(pointer: string): string {
  const parts = pointer.split('/');
  parts.pop();
  return parts.join('/');
}
